import { useEffect, useState } from "react";
import { getSessionBranchId, getSessionBranchName } from "../utils/branchSession";

// Branch-management role detection, mirroring the backend rules in
// employee/permissions.py. Reads session state populated by AuthContext.
//
// Role ladder (highest -> lowest):
//   super_admin     -> is_admin / is_super_admin  (org user; all branches)
//   branch_admin    -> is_branch_admin            (own branch only)
//   employee        -> everyone else (read-only unless delegated can_*)
//
// This hook decides what the UI SHOWS. The backend still enforces every rule,
// so a tampered client can never exceed these — the hook is UX, not security.
const readBool = (key) => {
    const v = sessionStorage.getItem(key);
    return v === "true" || v === true;
};

const readRole = () => {
    const isOrgAdmin = (readBool("is_super_admin") || readBool("is_admin")) && !readBool("is_branch_admin");
    const isBranchManager = readBool("is_branch_admin") && !isOrgAdmin;
    const role = isOrgAdmin ? "super_admin" : isBranchManager ? "branch_manager" : "employee";

    const canCreate = isOrgAdmin || isBranchManager || readBool("can_create");
    const canEdit = isOrgAdmin || isBranchManager || readBool("can_edit");
    const canDelete = isOrgAdmin || isBranchManager || readBool("can_delete");

    const branchId = getSessionBranchId();
    const branchName = getSessionBranchName();
    const branchAvail = readBool("branch_avail");

    return {
        role,
        isSuperAdmin: isOrgAdmin,
        isBranchManager,
        isEmployee: role === "employee",
        branchId,
        branchName,
        branchAvail,
        canManageBranches: isOrgAdmin,
        canCreateEmployee: canCreate,
        canEditEmployee: canEdit,
        canDeleteEmployee: canDelete,
        assignableRoles: isOrgAdmin
            ? ["employee", "branch_manager", "super_admin"]
            : isBranchManager
                ? ["employee", "branch_manager"]
                : [],
        canDelegateAccess: isOrgAdmin || isBranchManager,
    };
};

const useBranchRole = () => {
    const [state, setState] = useState(readRole);

    useEffect(() => {
        const refresh = () => setState(readRole());
        window.addEventListener("hrms:session-storage-hydrated", refresh);
        window.addEventListener("storage", refresh);
        return () => {
            window.removeEventListener("hrms:session-storage-hydrated", refresh);
            window.removeEventListener("storage", refresh);
        };
    }, []);

    return state;
};

export default useBranchRole;
